import { isEmpty } from 'lodash';

import { ServiceLocation } from '@dreryk/egabinet-healthcare-provider-frontend-api';
import { ReceptionServiceWithPractitioners } from '@dreryk/egabinet-private-medical-service-frontend-api';

import { assertUnreachable } from '@dreryk/react-common';

import { ReceptionServiceFormBean, ReceptionServiceFormController } from '../../ReceptionServiceForm';

import {
   WithMultipleServiceLocationController,
   WithMultipleServiceLocations,
   WithSingleServiceLocation,
   WithSingleServiceLocationController,
} from './serviceLocations.types';

export const serviceLocationControllerVisitor = (
   controller: ReceptionServiceFormController,
   serviceLocations: ServiceLocation[]
) => <ACCEPT>(visitor: {
   single?: (data: { controller: WithSingleServiceLocationController; serviceLocation: ServiceLocation }) => ACCEPT;
   multiple?: (data: {
      controller: WithMultipleServiceLocationController;
      serviceLocations: ServiceLocation[];
   }) => ACCEPT;
}) => {
   if (isEmpty(serviceLocations)) {
      return assertUnreachable(undefined);
   }

   if (serviceLocations.length === 1) {
      return visitor.single?.({
         controller: controller as WithSingleServiceLocationController,
         serviceLocation: serviceLocations.at(0)!,
      });
   }

   return visitor.multiple?.({
      controller: controller as WithMultipleServiceLocationController,
      serviceLocations: serviceLocations,
   });
};

export const receptionServiceLocationVisitor = (bean: ReceptionServiceFormBean) => <ACCEPT>(visitor: {
   withSingleServiceLocation: (serviceLocation: ServiceLocation | null) => ACCEPT;
   withMultipleServiceLocations: (serviceLocations: ServiceLocation[]) => ACCEPT;
}) => {
   if ('serviceLocation' in bean) {
      return visitor.withSingleServiceLocation(bean.serviceLocation!);
   }

   if ('serviceLocations' in bean) {
      return visitor.withMultipleServiceLocations(bean.serviceLocations.value);
   }

   return assertUnreachable(undefined);
};

export const mapReceptionServiceLocationsToBean = (
   data: ReceptionServiceWithPractitioners,
   serviceLocations: ServiceLocation[]
): WithSingleServiceLocation | WithMultipleServiceLocations => {
   if (isEmpty(serviceLocations)) {
      return assertUnreachable(undefined);
   }

   const selectedServiceLocationsIdsSet = new Set(data.serviceLocationIds.map((id) => id.asString()));

   if (serviceLocations.length === 1) {
      const foundServiceLocation = serviceLocations.find((serviceLocation) =>
         selectedServiceLocationsIdsSet.has(serviceLocation.id.asString())
      );

      return {
         serviceLocation: foundServiceLocation ?? null,
      };
   }

   const foundServiceLocations = serviceLocations.filter((serviceLocation) =>
      selectedServiceLocationsIdsSet.has(serviceLocation.id.asString())
   );

   return {
      serviceLocations: {
         selector: [],
         value: foundServiceLocations,
      },
   };
};
